// ============================================================
// AssetSpine.js — Single Source of Truth
// ============================================================
// Global configuration spine for every Grid vertical.
// All routes (/green-shield, /uprising, /moto, /sims, /training)
// import from THIS file only. Update once here, propagates
// everywhere automatically.
//
// Rule: NO video URLs, pricing tiers, or brand palette values
// live in any route file. If it belongs to more than one
// vertical, it belongs in the Spine.
// ============================================================

// ------------------------------------------------------------
// VIDEOS · every Amanda / Alfred / vertical hero
// ------------------------------------------------------------
export const VIDEOS = {
  // Amanda's Founder Greeting — hero of /green-shield.
  // Swap this line to push a new Amanda cut across every route
  // that references AMANDA_FOUNDER. Current: Amanda 4.0 v2 (Refined
  // Professional · Black Suit · 2026-02-20 recut).
  AMANDA_FOUNDER:
    "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/8d5e27a20f1f5392.mp4",

  // Uprising / Backstage Pass band hero.
  UPRISING_BAND:
    "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/634501042c02153a.mp4",

  // Alfred showreel / Grid activation bridge (shared clip).
  ALFRED_SHOWREEL:
    "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/31481dff275ace52.mp4",
};

// ------------------------------------------------------------
// DEMO_VIDEOS · Growth Engine cinematic showcase (10 clips)
// ------------------------------------------------------------
// The 10 real cinematic assets that render inside every vertical's
// GrowthEngine module via <DemoVideoCarousel />. Add/replace clips
// here — every vertical picks them up automatically.
export const DEMO_VIDEOS = [
  {
    id: "demo-01",
    title: "Property Transformation",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/6b1abec8d3f08ccd.mp4",
  },
  {
    id: "demo-02",
    title: "Professional Crew in Action",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/eab639f3475aeaa7.mp4",
  },
  {
    id: "demo-03",
    title: "Estate Gate Opening",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/9763dd981090618c.mp4",
  },
  {
    id: "demo-04",
    title: "Logistics Fleet Pulse",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/d749ea0a04b926b4.mp4",
  },
  {
    id: "demo-05",
    title: "Vintage Restoration Detail",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/ee76a50b9306fb5f.mp4",
  },
  {
    id: "demo-06",
    title: "Masonry Stone Placement",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/364876c1a2f31354.mp4",
  },
  {
    id: "demo-07",
    title: "Nightlife Cocktail Pour",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/00d9380d7bf674b9.mp4",
  },
  {
    id: "demo-08",
    title: "Shop Authority Tool Wall",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/0e122a6304c69408.mp4",
  },
  {
    id: "demo-09",
    title: "Boardroom Focus",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/b2fc3c0e8035d80a.mp4",
  },
  {
    id: "demo-10",
    title: "Cymbals / Stage Energy",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/9a7b217e07492168.mp4",
  },
];

// ------------------------------------------------------------
// LINKS · marketing surfaces & external destinations
// ------------------------------------------------------------
export const LINKS = {
  // Production home for the Founder Node CTA and any "learn more" link.
  FOUNDER_CTA: "https://wingmanalfred.com",
  WINGMAN_DEMO: "https://wingmanalfred.com/demo",
  // Internal onboarding target for every Gold "Claim Your Founder Node"
  // CTA across verticals. Uses a relative path so React Router handles
  // client-side navigation (no full page reload, no external redirect).
  JOIN_CTA: "/induction",
};

// ------------------------------------------------------------
// ROUTES · canonical vertical URLs
// ------------------------------------------------------------
// Trade-specific demo routes. Every email blast, SMS, or social
// push MUST reference these constants — never a hard-coded path.
// Update ROUTES here and every downstream template follows.
export const ROUTES = Object.freeze({
  LAWN:         "/green-shield",
  MUSIC:        "/uprising",
  MOTO:         "/moto",
  LOGISTICS:    "/sims",
  MOBILE_CYCLE: "/mobile-cycle",
  MIXOLOGY:     "/mixology",
  MAGIC_GARAGE: "/magic-garage",
  MSQUARED:     "/msquared",
  CIERRA:       "/cierra",
  FOUNDERS:     "/founders",
  TRAINING:     "/training",
  AFFORDABLE:   "/affordable",
});

// Same routes as absolute production URLs — email templates should
// reference PROD_URLS[key] so links resolve outside the app shell.
export const PROD_URLS = Object.freeze({
  LAWN:         `${LINKS.FOUNDER_CTA}${ROUTES.LAWN}`,
  MUSIC:        `${LINKS.FOUNDER_CTA}${ROUTES.MUSIC}`,
  MOTO:         `${LINKS.FOUNDER_CTA}${ROUTES.MOTO}`,
  LOGISTICS:    `${LINKS.FOUNDER_CTA}${ROUTES.LOGISTICS}`,
  MOBILE_CYCLE: `${LINKS.FOUNDER_CTA}${ROUTES.MOBILE_CYCLE}`,
  MIXOLOGY:     `${LINKS.FOUNDER_CTA}${ROUTES.MIXOLOGY}`,
  MAGIC_GARAGE: `${LINKS.FOUNDER_CTA}${ROUTES.MAGIC_GARAGE}`,
  MSQUARED:     `${LINKS.FOUNDER_CTA}${ROUTES.MSQUARED}`,
  CIERRA:       `${LINKS.FOUNDER_CTA}${ROUTES.CIERRA}`,
  FOUNDERS:     `${LINKS.FOUNDER_CTA}${ROUTES.FOUNDERS}`,
  TRAINING:     `${LINKS.FOUNDER_CTA}${ROUTES.TRAINING}`,
  AFFORDABLE:   `${LINKS.FOUNDER_CTA}${ROUTES.AFFORDABLE}`,
});

// Vertical metadata — label + canonical route + palette accent —
// so any content pipeline (email, social, in-app cards) can walk
// this list and render a full vertical roster without duplicating
// copy. Extend by appending here; downstream reads automatically.
export const VERTICALS = Object.freeze([
  { key: "LAWN",         label: "Dream Life Lawn",       trade: "Lawn Care",           route: ROUTES.LAWN,         prod_url: null, accent: "#22C55E" },
  { key: "MUSIC",        label: "The Uprising",          trade: "Live Music",          route: ROUTES.MUSIC,        prod_url: null, accent: "#F59E0B" },
  { key: "MOTO",         label: "Titan Motorcycle",      trade: "Motorcycle Service",  route: ROUTES.MOTO,         prod_url: null, accent: "#EF4444" },
  { key: "LOGISTICS",    label: "Sims Brothers",         trade: "Fleet Logistics",     route: ROUTES.LOGISTICS,    prod_url: null, accent: "#1E88E5" },
  { key: "MOBILE_CYCLE", label: "Mobile Cycle Works",    trade: "Vintage Restoration", route: ROUTES.MOBILE_CYCLE, prod_url: null, accent: "#8B5A3C" },
  { key: "MIXOLOGY",     label: "The Vault Room",        trade: "Craft Mixology",      route: ROUTES.MIXOLOGY,     prod_url: null, accent: "#C8934A" },
  { key: "MAGIC_GARAGE", label: "Magic Garage Door",     trade: "Garage Door Service", route: ROUTES.MAGIC_GARAGE, prod_url: null, accent: "#22D3EE" },
  { key: "MSQUARED",     label: "M Squared LLC",         trade: "Masonry & Stone",     route: ROUTES.MSQUARED,     prod_url: null, accent: "#D4A574" },
  { key: "CIERRA",       label: "Million Group Movement", trade: "Sovereign Vault",     route: ROUTES.CIERRA,       prod_url: null, accent: "#E4C065" },
  { key: "FOUNDERS",     label: "Last 50 Founders Club", trade: "Founders Utility",    route: ROUTES.FOUNDERS,     prod_url: null, accent: "#E8B04D" },
  { key: "TRAINING",     label: "Flight School",         trade: "Onboarding",          route: ROUTES.TRAINING,     prod_url: null, accent: "#F59E0B" },
  { key: "AFFORDABLE",   label: "Affordable Outdoor",    trade: "Property Management", route: ROUTES.AFFORDABLE,   prod_url: null, accent: "#2F6B3E" },
].map((v) => ({ ...v, prod_url: `${LINKS.FOUNDER_CTA}${v.route}` })));

// ------------------------------------------------------------
// BRAND PALETTES — 'Clean Windows' style tokens
// ------------------------------------------------------------
// Canonical Green Shield / Grid brand — Green + Black + Gold.
// Used by /green-shield and any vertical inheriting the core aesthetic.
export const BRAND = Object.freeze({
  GREEN:      "#22C55E",
  GREEN_DEEP: "#166534",
  BLACK:      "#0A0A0A",
  BLACK_HI:   "#0F1210",
  GOLD:       "#F59E0B",
  GOLD_DEEP:  "#B45309",
  MUTED:      "#B7C3B9",
  WHITE:      "#F8FAFC",
});

// Sims Brothers Recycling vertical — Black + Chrome + Sims Blue.
export const SIMS_PALETTE = Object.freeze({
  BLUE:       "#1E88E5",
  BLUE_DEEP:  "#0D47A1",
  CHROME:     "#C0C0C0",
  CHROME_DIM: "#7A7F87",
  BLACK:      "#050608",
  BLACK_HI:   "#0B0D12",
});

// Mobile Cycle Works · Vintage & Weathered — walnut / leather / brass.
export const VINTAGE_PALETTE = Object.freeze({
  GREEN:      "#8B5A3C",   // walnut (primary accent — mapped to "green" slot)
  GREEN_DEEP: "#4A2E1E",   // deep leather
  BLACK:      "#1A0F0A",   // deep matte cocoa-black
  BLACK_HI:   "#241812",
  GOLD:       "#B8860B",   // weathered brass
  GOLD_DEEP:  "#7A5A08",   // aged brass
  MUTED:      "#B8A488",   // parchment / aged cream
  WHITE:      "#EAD8B8",   // aged ivory
});

// ------------------------------------------------------------
// PALETTES · unified skin object per vertical
// ------------------------------------------------------------
// Every vertical route renders the same 6-module chassis
// (see /components/VerticalCockpit.jsx) — but each route pulls
// its own skin from here. Slot names are canonical:
//   PRIMARY / PRIMARY_DEEP  → "affirm" tone (Open / Available)
//   ACCENT  / ACCENT_DEEP   → "premium" tone (Booked / Featured / CTA)
//   BG      / BG_HI         → surface & elevated surface
//   MUTED                   → secondary text
//   INK                     → primary text
//   LABEL                   → header trade-line
// ------------------------------------------------------------
export const PALETTES = Object.freeze({
  // Dream Life Lawn — the reference implementation.
  LAWN: {
    PRIMARY:      "#22C55E",   // green
    PRIMARY_DEEP: "#166534",
    ACCENT:       "#F59E0B",   // gold
    ACCENT_DEEP:  "#B45309",
    BG:           "#0A0A0A",
    BG_HI:        "#0F1210",
    MUTED:        "#B7C3B9",
    INK:          "#F8FAFC",
    LABEL:        "Dream Life Lawn · Founder Grid",
    HEADER_TAIL:  "Amanda · Live on the Line",
  },

  // The Uprising — stage lighting: hot gold spotlight, deep crimson,
  // pure black. Feels like a concert. No greens anywhere.
  MUSIC: {
    PRIMARY:      "#F59E0B",   // spotlight gold
    PRIMARY_DEEP: "#7A5A08",
    ACCENT:       "#E11D48",   // stage crimson
    ACCENT_DEEP:  "#881337",
    BG:           "#08050A",
    BG_HI:        "#100812",
    MUTED:        "#9CA3AF",
    INK:          "#F8FAFC",
    LABEL:        "The Uprising · Founder Grid",
    HEADER_TAIL:  "Amanda · Live on the Line",
  },

  // Titan Motorcycle — shop floor: gunmetal, chrome silver, blue.
  // No greens. No golds. Purely metallic + electric blue.
  MOTO: {
    PRIMARY:      "#94A3B8",   // chrome silver
    PRIMARY_DEEP: "#475569",
    ACCENT:       "#0EA5E9",   // shop-floor blue
    ACCENT_DEEP:  "#075985",
    BG:           "#0B0F14",
    BG_HI:        "#111827",
    MUTED:        "#94A3B8",
    INK:          "#F8FAFC",
    LABEL:        "Titan Motorcycle · Founder Grid",
    HEADER_TAIL:  "Amanda · Live on the Line",
  },

  // Sims Brothers — industrial navy + safety orange.
  LOGISTICS: {
    PRIMARY:      "#F97316",   // safety orange
    PRIMARY_DEEP: "#9A3412",
    ACCENT:       "#38BDF8",   // navy-cool
    ACCENT_DEEP:  "#0369A1",
    BG:           "#050810",
    BG_HI:        "#0B1120",
    MUTED:        "#94A3B8",
    INK:          "#F8FAFC",
    LABEL:        "Sims Brothers · Founder Grid",
    HEADER_TAIL:  "Amanda · Live on the Line",
  },

  // Mobile Cycle Works, Inc. (Ashley, OH) — Vintage & Weathered.
  // Warm walnut, aged leather, weathered brass. Deep cocoa base.
  MOBILE_CYCLE: {
    PRIMARY:      "#B8860B",   // weathered brass
    PRIMARY_DEEP: "#7A5A08",
    ACCENT:       "#8B5A3C",   // walnut
    ACCENT_DEEP:  "#4A2E1E",
    BG:           "#1A0F0A",
    BG_HI:        "#241812",
    MUTED:        "#B8A488",
    INK:          "#EAD8B8",
    LABEL:        "Mobile Cycle Works · Founder Grid",
    HEADER_TAIL:  "Amanda · On the Bench",
  },

  // The Vault Room — Craft Mixology · "Top Shelf" aesthetic.
  // Polished mahogany + aged leather + whiskey amber + crystal glint.
  // Speakeasy-elegant. No greens, no oranges — pure nightlife authority.
  MIXOLOGY: {
    PRIMARY:      "#C8934A",   // aged whiskey amber
    PRIMARY_DEEP: "#8B5A2B",   // dark bourbon
    ACCENT:       "#E8E4D9",   // crystal glint (frosted glass rim)
    ACCENT_DEEP:  "#9E9A8F",   // aged silver
    BG:           "#1A0E08",   // polished mahogany black
    BG_HI:        "#241811",
    MUTED:        "#B8A488",   // whiskey haze
    INK:          "#F5EAD8",   // candlelight cream
    LABEL:        "The Vault Room · Founder Grid",
    HEADER_TAIL:  "Amanda · On the Rail",
  },

  // Magic Garage Door (Mansfield, OH) — "Industrial Steel" aesthetic.
  // Electric-cyan spark + chrome-white + warm charcoal. Distinct from
  // MOTO's cooler shop palette. Reads like a fresh spring on the coil.
  MAGIC_GARAGE: {
    PRIMARY:      "#22D3EE",   // electric cyan (the "spark")
    PRIMARY_DEEP: "#0E7490",
    ACCENT:       "#E2E8F0",   // chrome white
    ACCENT_DEEP:  "#64748B",   // dark steel
    BG:           "#0A0E13",   // warm charcoal
    BG_HI:        "#131822",
    MUTED:        "#94A3B8",
    INK:          "#F8FAFC",
    LABEL:        "Magic Garage Door · Founder Grid",
    HEADER_TAIL:  "Amanda · On the Line",
  },

  // M Squared LLC (Marion, OH) — "Masonry & Stone" aesthetic.
  // Warm sandstone + slate + industrial grey. Grounded, load-bearing.
  MSQUARED: {
    PRIMARY:      "#D4A574",   // sandstone / limestone warm
    PRIMARY_DEEP: "#8B6B47",
    ACCENT:       "#64748B",   // slate
    ACCENT_DEEP:  "#334155",
    BG:           "#1C1E22",   // industrial grey black
    BG_HI:        "#26292E",
    MUTED:        "#94A3B8",
    INK:          "#F1F5F9",
    LABEL:        "M Squared LLC · Founder Grid",
    HEADER_TAIL:  "Amanda · On the Site",
  },

  // The Million Group Movement · Sovereign Vault — "Gold Standard".
  // High-contrast: warm gold + pure white + deep charcoal.
  // Editorial, executive, legacy-grade. Reads like a private club.
  CIERRA: {
    PRIMARY:      "#E4C065",   // warm gold
    PRIMARY_DEEP: "#8B6914",
    ACCENT:       "#FFFFFF",   // pure white
    ACCENT_DEEP:  "#D4D4D4",
    BG:           "#0A0A0A",   // deep charcoal near-black
    BG_HI:        "#1A1A1A",
    MUTED:        "#A8A8A8",
    INK:          "#FFFFFF",
    LABEL:        "Million Group Movement · Sovereign Vault",
    HEADER_TAIL:  "Amanda · In the Vault",
  },

  // The Last 50 Founders Club — "Helping Hand" aesthetic.
  // Warm walnut wood + honey amber + clean-windows off-white.
  // Reads like a neighborhood office where a friend gives you the
  // straight answer. Human. Generous. Load-bearing but never cold.
  FOUNDERS: {
    PRIMARY:      "#E8B04D",   // honey / warm amber (affirming, generous)
    PRIMARY_DEEP: "#A0762A",
    ACCENT:       "#F5EFE1",   // clean-windows off-white (light, clear)
    ACCENT_DEEP:  "#C9BFA5",   // aged cream
    BG:           "#1C1409",   // deep walnut
    BG_HI:        "#261C10",
    MUTED:        "#C9BFA5",
    INK:          "#F5EFE1",
    LABEL:        "The Last 50 Founders Club",
    HEADER_TAIL:  "Amanda · Answering the Line",
  },

  // Affordable Outdoor Property Management (Ohio) — "Earth & Stone" aesthetic.
  // Deep forest green + slate grey + granite/cream accent. Property
  // management authority — rugged, load-bearing, professional. Reads
  // like a boots-on-gravel command post, not a boutique lawn service.
  AFFORDABLE: {
    PRIMARY:      "#2F6B3E",   // deep forest green (affirming — Open / Available)
    PRIMARY_DEEP: "#1A3D22",   // pine shadow
    ACCENT:       "#C9B27C",   // weathered cream / granite highlight (Booked / CTA)
    ACCENT_DEEP:  "#8A7548",   // aged brass on stone
    BG:           "#12181A",   // deep slate near-black
    BG_HI:        "#1B2225",   // granite-charcoal elevated surface
    MUTED:        "#A7B0AB",   // moss-touched stone grey
    INK:          "#EAEEEB",   // limestone off-white
    LABEL:        "Affordable Outdoor Property Management · Founder Grid",
    HEADER_TAIL:  "Amanda · On the Line",
  },
});

// ------------------------------------------------------------
// PRICING TIERS
// ------------------------------------------------------------
// The 1-2-12 Founder Terms — canonical pricing ladder.
// This is the ONLY subscription pricing surface across the app.
export const FOUNDER_TIMELINE = Object.freeze([
  {
    key: "m1",
    tag: "Month 1",
    amount: "$0",
    label: "Release Phase",
    body:
      "Agent-assisted setup. We build and configure everything for " +
      "you. Amanda goes live on your line the same week.",
    tone: BRAND.GREEN,
  },
  {
    key: "m2",
    tag: "Month 2",
    amount: "$97",
    label: "Pilot Phase",
    body:
      "Your system goes live. First real customers. Every module — " +
      "Booking, Calendar, Billing, Notes, Ads — running end-to-end.",
    tone: BRAND.GOLD,
  },
  {
    key: "m12",
    tag: "Year 1",
    amount: "$297/mo",
    label: "Full Utility Lock",
    body:
      "Rest of Year 1 onward. Founder rate locked for life. New " +
      "Founder Nodes after Q1 2026 pay the full going rate.",
    tone: BRAND.GOLD,
  },
]);

// Uprising / Backstage Pass fan-loop tier.
export const UPRISING_JOIN_PRICE = "$97";

// ------------------------------------------------------------
// Copy tokens that surface in more than one vertical.
// ------------------------------------------------------------
export const COPY = Object.freeze({
  FOUNDER_BADGE_LINE: "Locked In for Life.",
  FOUNDER_CTA_LABEL:  "Claim Your Founder Node — Start Free",
  FOUNDER_KICKER:     "Grid Founder",
});

// ============================================================
// SEO · Social Identity Spine
// ============================================================
// Single source of truth for every route's <title>, meta
// description, canonical URL, OG image, and Twitter card.
// The <SeoHelmet routeKey="..."/> component reads from this
// object and injects the tags into <head>. Update once here,
// propagates everywhere automatically. Do NOT hardcode meta
// tags in individual page files.
//
// Notes on OG images:
//   • Files live under /app/frontend/public/og/ so they resolve
//     as absolute paths under LINKS.FOUNDER_CTA at deploy time.
//   • Missing renders fall back to OG_IMAGES.DEFAULT (Amanda
//     4.0 Black Suit foyer render) so a share link is never
//     tagless.
//   • Social crawlers (Facebook, LinkedIn, Twitter) read the
//     static tags in public/index.html — those exist as
//     SITE-WIDE fallbacks. Helmet updates the client-side
//     title/description for the modern Google crawler and
//     in-app UX.
// ------------------------------------------------------------
export const OG_IMAGES = Object.freeze({
  // Amanda 4.0 · Black Suit foyer render (site-wide default + home).
  DEFAULT:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/7ca9bc0e719fa488874ccf62556e49192a5efd7788a1bc2ec15cc90c075c0620.jpeg",
  AMANDA_FOYER:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/7ca9bc0e719fa488874ccf62556e49192a5efd7788a1bc2ec15cc90c075c0620.jpeg",
  // Last 50 Founders Club render.
  FOUNDERS:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/1e429b5c43f6a77b3b219905360ac337a8c19498c740c9839bf6453c3df86c20.jpeg",
  // Million Group Gold render (Cierra Sovereign Vault).
  MILLION_GOLD:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/c02b0c8ec2d276959d701576479c58613b2818f6af4bc5ecedf3c627a6313782.jpeg",
  // Green Shield / Dream Life Lawn render.
  GREEN_SHIELD:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/9158fdc3e2c930868c141b1cb1e2977238258a012d1409612278a09a7eb77daa.jpeg",
  // Industrial Steel render (Magic Garage Door).
  INDUSTRIAL_STEEL:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/6dbcff3417b7627cea0a004a797ea711fc9cd5936e68225f9d75302226edd43d.jpeg",
});

export const SEO_DEFAULTS = Object.freeze({
  SITE_NAME:   "Wingman Alfred Academy",
  BASE_URL:    LINKS.FOUNDER_CTA,                     // https://wingmanalfred.com
  DESCRIPTION: "We stabilize your back-office, answer your phones, and shield your leads for a flat monthly utility rate. Stop the AI confusion.",
  OG_IMAGE:    OG_IMAGES.DEFAULT,
  TWITTER_CARD:"summary_large_image",
  LOCALE:      "en_US",
  TYPE:        "website",
});

// Per-route SEO records. Key = route path (matches ROUTES values).
// Every active production route MUST have an entry. Missing keys
// fall back to SEO_DEFAULTS.
export const SEO = Object.freeze({
  "/": {
    title:       "Wingman Alfred | The AI Utility for Small Business",
    description: SEO_DEFAULTS.DESCRIPTION,
    canonical:   `${SEO_DEFAULTS.BASE_URL}/`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/founders": {
    title:       "The Last 50 Founders Club | National Small Business AI Utility",
    description: "Join the last 50 founders locking in the $297/mo utility rate for life. Amanda answers every line, files every quote, protects every lead — nationwide.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.FOUNDERS}`,
    ogImage:     OG_IMAGES.FOUNDERS,
  },
  "/cierra": {
    title:       "Cierra Lueck Sovereign Vault | The Million Group Movement",
    description: "Cierra Lueck's Sovereign Vault — the Million Group Movement's private utility spine. Legacy-grade back-office. Amanda in the vault, on the line, 24/7.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.CIERRA}`,
    ogImage:     OG_IMAGES.MILLION_GOLD,
  },
  "/green-shield": {
    title:       "Todd Van Dyne's Command Center | Dream Life Lawn & Landscape",
    description: "Dream Life Lawn & Landscape · Founder Grid. Amanda captures every mow request, plots the routes overnight, and files every invoice. Todd Van Dyne's command center.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.LAWN}`,
    ogImage:     OG_IMAGES.GREEN_SHIELD,
  },
  "/magic-garage": {
    title:       "Sabrina Wyeth's AI Service Director | Magic Garage Door",
    description: "Magic Garage Door · Mansfield, OH. Amanda answers every call to 419-529-2002, books on-site quotes, and files every emergency ticket. Sabrina Wyeth's service director.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.MAGIC_GARAGE}`,
    ogImage:     OG_IMAGES.INDUSTRIAL_STEEL,
  },
  "/uprising": {
    title:       "Uprising Band Backstage Pass | Ohio Rock Authority",
    description: "The Uprising · Backstage Pass. Amanda books every fan meet-and-greet, texts QR passes, and settles the guarantee. Ohio's rock authority, on the Grid.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.MUSIC}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/sims": {
    title:       "Sims Brothers Logistics Command | Industrial Fleet Utility",
    description: "Sims Brothers · Fleet Logistics Command. Amanda books every pickup, texts every BOL, and settles every freight bill. Industrial-grade utility spine.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.LOGISTICS}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/moto": {
    title:       "Vintage Restoration Spine | Motorcycle Shop Utility",
    description: "Titan Motorcycle · Founder Grid. Amanda reserves every bay, texts every drop-off ticket, and files every service. Motorcycle shop utility, on the line.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.MOTO}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/mixology": {
    title:       "The Vault Room | Craft Mixology · Founder Grid",
    description: "The Vault Room · Craft Mixology. Amanda answers every reservation call, texts confirmations to every guest, and files every tab in the Grid. Speakeasy authority on the rail.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.MIXOLOGY}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/msquared": {
    title:       "Bob Wyeth's Site Command | M Squared LLC Masonry & Stone",
    description: "M Squared LLC · Marion, OH. Amanda answers every bid call to 740-514-4652, books every site walk, and files every milestone draw. Bob Wyeth's masonry command post.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.MSQUARED}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/mobile-cycle": {
    title:       "Vintage Restoration Spine | Motorcycle Shop Utility",
    description: "Mobile Cycle Works · Ashley, OH. Amanda reserves every bench, texts every drop-off card, and files every restoration. Vintage restoration spine, on the bench.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.MOBILE_CYCLE}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/blueprint": {
    title:       "The Grid Sales Blueprint | Wingman Training Portal",
    description: "The Grid Sales Blueprint. Every objection, every close, every founder pitch — worked live. Wingman training portal for the sales rep spine.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}/blueprint`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/training": {
    title:       "Wingman Training Portal | AI Utility",
    description: "Wingman Training Portal. Flight School onboarding for every founder and rep on the Grid. The AI utility spine, taught by Alfred and Amanda themselves.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.TRAINING}`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
  "/affordable": {
    title:       "Elvis' Property Command Post | Affordable Outdoor Property Management",
    description: "Affordable Outdoor Property Management · Ohio. Amanda answers every property inquiry to 419-529-2002, dispatches every mow and maintenance crew, and files every recurring invoice. Elvis' property command post.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}${ROUTES.AFFORDABLE}`,
    ogImage:     OG_IMAGES.GREEN_SHIELD,
  },
  "/induction": {
    title:       "Founder Induction · The Digital Handshake | Wingman Alfred",
    description: "Sign the 7-Hour Handshake and activate your Founder Node. $0 today · $97 Month 2 · $297/mo Year 1 — locked for life. Ohio Digital Grid · Founder Utility.",
    canonical:   `${SEO_DEFAULTS.BASE_URL}/induction`,
    ogImage:     OG_IMAGES.AMANDA_FOYER,
  },
});
